import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { quoteRequestService } from '../../services/quoteRequestService';
import QuoteRequestDetailsModal from './components/QuoteRequestDetailsModal';
import QuoteRequestFilters from './components/QuoteRequestFilters';

const QuoteRequestsManagement = () => {
    const navigate = useNavigate();
    const [quoteRequests, setQuoteRequests] = useState([]);
    const [filteredRequests, setFilteredRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [stats, setStats] = useState(null);
    const [filters, setFilters] = useState({
        status: '',
        requestType: '',
        search: ''
    });

    useEffect(() => {
        fetchQuoteRequests();
        fetchStats();

        // Subscribe to real-time updates
        const unsubscribe = quoteRequestService?.subscribeToQuoteRequests(() => {
            fetchQuoteRequests();
            fetchStats();
        });

        return () => unsubscribe?.();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [filters, quoteRequests]);

    const fetchQuoteRequests = async () => {
        try {
            setLoading(true);
            const data = await quoteRequestService?.getAllQuoteRequests();
            setQuoteRequests(data);
        } catch (error) {
            console.error('Error fetching quote requests:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const data = await quoteRequestService?.getQuoteRequestStats();
            setStats(data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const applyFilters = () => {
        let filtered = [...quoteRequests];

        if (filters?.status) {
            filtered = filtered?.filter(r => r?.status === filters?.status);
        }

        if (filters?.requestType) {
            filtered = filtered?.filter(r => r?.requestType === filters?.requestType);
        }

        if (filters?.search) {
            const searchLower = filters?.search?.toLowerCase();
            filtered = filtered?.filter(r =>
                r?.customerName?.toLowerCase()?.includes(searchLower) ||
                r?.customerEmail?.toLowerCase()?.includes(searchLower) ||
                r?.quoteNumber?.toLowerCase()?.includes(searchLower)
            );
        }

        setFilteredRequests(filtered);
    };

    const handleViewDetails = (request) => {
        setSelectedRequest(request);
        setShowDetailsModal(true);
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await quoteRequestService?.updateQuoteRequestStatus(id, newStatus);
            fetchQuoteRequests();
            fetchStats();
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) return;

        try {
            await quoteRequestService?.deleteQuoteRequest(id);
            fetchQuoteRequests();
            fetchStats();
        } catch (error) {
            console.error('Error deleting quote request:', error);
        }
    };

    return (
        <>
        <Header />
        <div className="min-h-screen bg-background pt-24 md:pt-28">
            {/* Header */}
            <div className="bg-card border-b border-border">
                <div className="container mx-auto px-4 py-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold text-foreground">Demandes de Devis</h1>
                            <p className="text-muted-foreground mt-1">
                                Gérez toutes les demandes de devis des clients
                            </p>
                        </div>
                        <Button
                            variant="outline"
                            iconName="ArrowLeft"
                            iconPosition="left"
                            onClick={() => navigate('/protected-admin-dashboard')}
                        >
                            Retour au Tableau de Bord
                        </Button>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="container mx-auto px-4 py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-card border border-border rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Total</p>
                                    <p className="text-2xl font-bold text-foreground">{stats?.total}</p>
                                </div>
                                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                    <Icon name="FileText" size={24} className="text-blue-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">En Attente</p>
                                    <p className="text-2xl font-bold text-foreground">{stats?.pending}</p>
                                </div>
                                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                                    <Icon name="Clock" size={24} className="text-yellow-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Acceptées</p>
                                    <p className="text-2xl font-bold text-foreground">{stats?.accepted}</p>
                                </div>
                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                    <Icon name="CheckCircle2" size={24} className="text-green-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Véhicules / Immobilier</p>
                                    <p className="text-2xl font-bold text-foreground">
                                        {stats?.vehicleBookings} / {stats?.propertyRentals}
                                    </p>
                                </div>
                                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                    <Icon name="PieChart" size={24} className="text-purple-600" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="container mx-auto px-4 py-6">
                {/* Filters */}
                <QuoteRequestFilters
                    filters={filters}
                    onFiltersChange={setFilters}
                    onRefresh={fetchQuoteRequests}
                />

                {/* Table */}
                <div className="bg-card border border-border rounded-lg overflow-hidden mt-6">
                    {loading ? (
                        <div className="p-12 text-center">
                            <Icon name="Loader2" size={40} className="animate-spin mx-auto text-muted-foreground" />
                            <p className="text-muted-foreground mt-4">Chargement...</p>
                        </div>
                    ) : filteredRequests?.length === 0 ? (
                        <div className="p-12 text-center">
                            <Icon name="Inbox" size={40} className="mx-auto text-muted-foreground" />
                            <p className="text-muted-foreground mt-4">Aucune demande de devis trouvée</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-muted/50 border-b border-border">
                                    <tr>
                                        <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Référence
                                        </th>
                                        <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:table-cell">
                                            Type
                                        </th>
                                        <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Client
                                        </th>
                                        <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden md:table-cell">
                                            Date
                                        </th>
                                        <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:table-cell">
                                            Montant
                                        </th>
                                        <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Statut
                                        </th>
                                        <th className="px-3 md:px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-card divide-y divide-border">
                                    {filteredRequests?.map((request) => (
                                        <tr key={request?.id} className="hover:bg-muted/30 transition-colors">
                                            <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-foreground">{request?.quoteNumber}</div>
                                            </td>
                                            <td className="px-3 md:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                                                <div className="text-sm text-foreground">
                                                    {quoteRequestService?.getTypeLabel(request?.requestType)}
                                                </div>
                                            </td>
                                            <td className="px-3 md:px-6 py-4">
                                                <div className="text-sm text-foreground font-medium">{request?.customerName}</div>
                                                <div className="text-xs text-muted-foreground">{request?.customerEmail}</div>
                                            </td>
                                            <td className="px-3 md:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                                                <div className="text-sm text-foreground">
                                                    {new Date(request?.createdAt)?.toLocaleDateString('fr-FR')}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {new Date(request?.createdAt)?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </td>
                                            <td className="px-3 md:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                                                <div className="text-sm font-medium text-foreground">
                                                    {quoteRequestService?.formatAmount(request?.totalAmount, request?.currency)}
                                                </div>
                                            </td>
                                            <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${quoteRequestService?.getStatusColor(request?.status)}`}>
                                                    {quoteRequestService?.getStatusLabel(request?.status)}
                                                </span>
                                            </td>
                                            <td className="px-3 md:px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                                <button
                                                    onClick={() => handleViewDetails(request)}
                                                    className="text-blue-600 hover:text-blue-900 inline-flex items-center gap-1"
                                                >
                                                    <Icon name="Eye" size={16} />
                                                    <span>Voir</span>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(request?.id)}
                                                    className="text-red-600 hover:text-red-900 inline-flex items-center gap-1"
                                                >
                                                    <Icon name="Trash2" size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Details Modal */}
            {showDetailsModal && selectedRequest && (
                <QuoteRequestDetailsModal
                    isOpen={showDetailsModal}
                    onClose={() => {
                        setShowDetailsModal(false);
                        setSelectedRequest(null);
                    }}
                    request={selectedRequest}
                    onStatusChange={handleStatusChange}
                    onRefresh={fetchQuoteRequests}
                />
            )}
        </div>
        <Footer />
        </>
    );
};

export default QuoteRequestsManagement;
